import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Check, CircleSlash, Loader2, Undo2, X } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { useOp } from "../../lib/useOp";
import { CHIP } from "../../lib/copiloto";

export const UiChip = ({ a }) => {
  const nav = useNavigate();
  if (a.tipo === "limite") return <button onClick={() => nav(`${a.ruta}${a.ruta.includes("?") ? "&" : "?"}resaltar=${encodeURIComponent(a.resaltar || "")}`)} data-testid="ai-limite-ir"
    className="mr-1 mt-1 inline-flex items-center gap-1 rounded-full bg-[#E8643C] px-3 py-1.5 text-[11px] font-semibold text-white">Ir a la pantalla · {a.etiqueta} (lo ejecuta una persona)</button>;
  return <UiChipBase a={a} />;
};
const UiChipBase = ({ a }) => (
  <span className="mr-1 mt-1 inline-flex items-center gap-1 rounded-full bg-[#13315C]/8 px-2.5 py-1 text-[11px] font-semibold text-[#13315C]" data-testid="ai-ui-chip">
    ↗ {CHIP[a.tipo] || a.tipo}{a.etiqueta && a.tipo !== "vista_guardada" ? ` · ${a.etiqueta}` : ""}
  </span>
);

const useRestante = (hasta) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!hasta) return undefined;
    const f = () => setT(Math.max(0, Math.floor((new Date(hasta) - Date.now()) / 1000)));
    f();
    const i = setInterval(f, 1000);
    return () => clearInterval(i);
  }, [hasta]);
  return t;
};

export const Deshacer = ({ url, hasta, onDone, testid }) => {
  const s = useRestante(hasta);
  const [busy, setBusy] = useState(false);
  if (!hasta || s <= 0) return null;
  const go = async () => { setBusy(true); try { await http.post(url); toast.success("Cambios deshechos"); onDone?.(); } catch (e) { toast.error(errMsg(e)); } setBusy(false); };
  return <button onClick={go} disabled={busy} data-testid={testid} className="mt-2 inline-flex items-center gap-1 rounded-full border border-[#13315C]/30 px-3 py-1.5 text-xs font-semibold text-[#13315C] hover:bg-[#13315C]/5">
    {busy ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}Deshacer ({Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")})</button>;
};

const Icono = ({ e }) => e === "ok" ? <Check size={13} className="text-emerald-600" /> : e === "error" ? <X size={13} className="text-rose-600" /> : <span className="h-2 w-2 rounded-full bg-[#C9C3B8]" />;

export const PlanCard = ({ plan }) => {
  const [p, setP] = useState(plan);
  const [masivo, setMasivo] = useState(false);
  const { run, bar, busy } = useOp(`plan-${plan.id}`);
  const confirmar = async () => {
    try {
      const r = await run(`Plan: ${p.titulo}`, (o) => http.post(`/copiloto/planes/${p.id}/confirmar`, { masivo_confirmado: masivo }, o));
      setP({ ...p, ...r.data });
      toast[r.data.estado === "ejecutado" ? "success" : "error"](r.data.estado === "ejecutado" ? "Plan ejecutado" : "El plan se detuvo en el primer error");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cancelar = async () => { try { await http.post(`/copiloto/planes/${p.id}/cancelar`); setP({ ...p, estado: "cancelado" }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="my-2 overflow-hidden rounded-2xl border border-[#13315C]/20 bg-white text-sm shadow-sm" data-testid={`ai-plan-${p.id}`}>
      <div className="bg-[#0B2545] px-4 py-2.5 text-white"><p className="text-[10px] font-semibold uppercase tracking-widest text-[#E8643C]">Plan del copiloto · confirmación requerida</p><p className="font-semibold">{p.titulo}</p></div>
      <ol className="divide-y divide-[#F2F0EC] px-4">{p.pasos.map((s, i) => (
        <li key={i} className="flex items-start gap-2 py-2" data-testid={`ai-plan-paso-${i}`}>
          <span className="mt-1"><Icono e={s.estado} /></span>
          <div className="min-w-0 flex-1"><p className="font-medium">{i + 1}. {s.descripcion}</p>
            <p className="text-[11px] text-[#5C5850]">{s.label} · {s.registros} registro{s.registros === 1 ? "" : "s"} · {s.reversible ? "reversible" : <b className="text-[#B4532A]">irreversible</b>}{s.detalle ? ` · ${s.detalle}` : ""}</p></div>
        </li>))}</ol>
      <div className="border-t border-[#F2F0EC] px-4 py-3">
        {bar}
        {p.estado === "pendiente" ? <>
          {p.masivo && <label className="mb-2 flex items-start gap-2 rounded-xl bg-amber-50 p-2 text-xs text-amber-900"><input type="checkbox" checked={masivo} onChange={(e) => setMasivo(e.target.checked)} data-testid={`ai-plan-masivo-${p.id}`} className="mt-0.5" />
            <span><AlertTriangle size={12} className="mr-1 inline" />Operación masiva: {p.total_registros} registros. Confirmo que quiero aplicarla.</span></label>}
          <div className="flex gap-2"><button onClick={confirmar} disabled={busy || (p.masivo && !masivo)} data-testid={`ai-plan-confirmar-${p.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#0B2545] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#E8643C] disabled:opacity-40"><Check size={13} />Confirmar</button>
            <button onClick={cancelar} data-testid={`ai-plan-cancelar-${p.id}`} className="rounded-full border border-[#E2DDD5] px-3 py-1.5 text-xs font-semibold">Cancelar</button></div></>
          : <p className={`flex items-center gap-1 text-xs font-semibold ${p.estado === "ejecutado" ? "text-emerald-700" : p.estado === "deshecho" || p.estado === "cancelado" ? "text-[#5C5850]" : "text-rose-700"}`} data-testid={`ai-plan-estado-${p.id}`}>
            {p.estado === "cancelado" && <CircleSlash size={12} />}{p.estado}</p>}
        {p.estado !== "deshecho" && <Deshacer url={`/copiloto/planes/${p.id}/deshacer`} hasta={p.deshacer_hasta} onDone={() => setP({ ...p, estado: "deshecho", deshacer_hasta: null })} testid={`ai-plan-deshacer-${p.id}`} />}
      </div>
    </div>
  );
};
